
import { Module } from './types';
import { StandardQuizQuestion } from '@/types/quizTypes';

// Enhanced React modules with comprehensive quiz content
export const ENHANCED_REACT_INTRO_MODULE: Module = {
  id: 'react-introduction',
  title: 'Introduction to React',
  description: 'Learn the fundamentals of React and why it\'s the most popular JavaScript library for building user interfaces.',
  content: `
# Introduction to React

React is a powerful JavaScript library for building user interfaces, developed by Facebook (now Meta).

## What is React?

React is a **declarative**, **efficient**, and **flexible** JavaScript library for building user interfaces. It lets you compose complex UIs from small and isolated pieces of code called "components."

## Key Benefits of React

### 1. Component-Based Architecture
- Build encapsulated components that manage their own state
- Compose them to make complex UIs
- Reusable components save development time

### 2. Virtual DOM
- React creates an in-memory virtual DOM
- Efficiently updates only what needs to change
- Results in better performance

### 3. Learn Once, Write Anywhere
- Create new features without rewriting existing code
- Render on the server using Node
- Power mobile apps using React Native

## React Ecosystem

React works well with:
- **Create React App**: Quick project setup
- **Next.js**: Full-stack React framework
- **Redux**: State management
- **React Router**: Navigation
- **Material-UI**: Component library

## Getting Started

To create a new React application:

\`\`\`bash
npx create-react-app my-app
cd my-app
npm start
\`\`\`

## Your First Component

\`\`\`jsx
function Welcome(props) {
  return <h1>Hello, {props.name}!</h1>;
}

function App() {
  return (
    <div>
      <Welcome name="Alice" />
      <Welcome name="Bob" />
    </div>
  );
}
\`\`\`

This creates two instances of the Welcome component with different props.
  `,
  duration: '45 minutes',
  difficulty: 'beginner',
  order: 1,
  estimatedTime: '45 minutes',
  objectives: [
    'Understand what React is and its core benefits',
    'Learn about component-based architecture',
    'Understand the Virtual DOM concept',
    'Set up your first React application'
  ],
  learningObjectives: [
    'Understand what React is and its core benefits',
    'Learn about component-based architecture', 
    'Understand the Virtual DOM concept',
    'Set up your first React application'
  ],
  quiz: {
    questions: [
      {
        id: 'react-intro-1',
        question: 'What is React primarily used for?',
        options: [
          'Building user interfaces',
          'Server-side programming',
          'Database management',
          'Network security'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0,
        difficulty: 'easy',
        explanation: 'React is a JavaScript library specifically designed for building user interfaces, particularly web applications. It excels at creating interactive and dynamic UIs.',
        category: 'fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: 'react-intro-2',
        question: 'What is the Virtual DOM in React?',
        options: [
          'A copy of the real DOM kept in memory for efficient updates',
          'A new type of HTML element',
          'A JavaScript framework',
          'A CSS preprocessor'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0,
        difficulty: 'medium',
        explanation: 'The Virtual DOM is React\'s in-memory representation of the real DOM. It allows React to efficiently calculate what changes need to be made and update only those parts of the real DOM, resulting in better performance.',
        category: 'concepts',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: 'react-intro-3',
        question: 'Which company originally developed React?',
        options: [
          'Google',
          'Facebook (Meta)',
          'Microsoft',
          'Twitter'
        ],
        correctAnswer: 1,
        correctAnswerIndex: 1,
        difficulty: 'easy',
        explanation: 'React was originally developed by Facebook (now Meta) and was first released in 2013. It was created by Jordan Walke, a software engineer at Facebook.',
        category: 'history',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: 'react-intro-4',
        question: 'What makes React components reusable?',
        options: [
          'They can accept different props and manage their own state',
          'They are written in JavaScript',
          'They use HTML tags',
          'They have CSS styling'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0,
        difficulty: 'medium',
        explanation: 'React components are reusable because they can accept different props (properties) and manage their own internal state. This allows the same component logic to be used in multiple contexts with different data.',
        category: 'components',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: 'react-intro-5',
        question: 'What is the main benefit of React\'s component-based architecture?',
        options: [
          'Easier to build, maintain, and test applications',
          'Faster internet connection',
          'Better graphics rendering',
          'Automatic database management'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0,
        difficulty: 'medium',
        explanation: 'Component-based architecture makes applications easier to build, maintain, and test because each component is self-contained and can be developed, tested, and debugged independently.',
        category: 'architecture',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: 'react-intro-6',
        question: 'Which tool is recommended for creating a new React application?',
        options: [
          'Create React App',
          'Notepad',
          'Microsoft Word',
          'Paint'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0,
        difficulty: 'easy',
        explanation: 'Create React App is the official tool recommended by the React team for setting up new React applications. It provides a modern build setup with no configuration required.',
        category: 'tools',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: 'react-intro-7',
        question: 'What does "Learn Once, Write Anywhere" mean in the context of React?',
        options: [
          'React knowledge can be applied to web, mobile, and server applications',
          'React only works on websites',
          'React is only for desktop applications',
          'React requires learning different languages for different platforms'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0,
        difficulty: 'hard',
        explanation: '"Learn Once, Write Anywhere" means that once you learn React, you can use that knowledge to build web applications, mobile apps (with React Native), and even server-side rendered applications.',
        category: 'ecosystem',
        points: 20,
        type: 'multiple-choice'
      },
      {
        id: 'react-intro-8',
        question: 'How does React improve application performance?',
        options: [
          'By using Virtual DOM to minimize real DOM updates',
          'By running on faster servers',
          'By compressing images automatically',
          'By reducing internet bandwidth usage'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0,
        difficulty: 'hard',
        explanation: 'React improves performance by using the Virtual DOM to calculate the minimum changes needed and then efficiently updating only those parts of the real DOM, rather than re-rendering entire sections.',
        category: 'performance',
        points: 20,
        type: 'multiple-choice'
      }
    ],
    passThreshold: 80,
    timeLimit: 600,
    category: 'react'
  },
  quickCheck: {
    questions: [
      {
        id: 'react-intro-quick-1',
        question: 'What is React?',
        options: [
          'A JavaScript library for building user interfaces',
          'A database system',
          'An operating system',
          'A web browser'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0
      },
      {
        id: 'react-intro-quick-2',
        question: 'What is the Virtual DOM?',
        options: [
          'A memory representation of the real DOM',
          'A new HTML standard',
          'A CSS framework',
          'A JavaScript engine'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0
      }
    ],
    passThreshold: 80
  }
};

export const ENHANCED_REACT_COMPONENTS_MODULE: Module = {
  id: 'react-components',
  title: 'React Components',
  description: 'Master the fundamentals of React components, the building blocks of React applications.',
  content: `
# React Components

Components are the building blocks of React applications. They let you split the UI into independent, reusable pieces.

## Types of Components

### Functional Components
The modern, recommended way to write components:

\`\`\`jsx
function Greeting(props) {
  return <h1>Hello, {props.name}!</h1>;
}

// Arrow function syntax
const Greeting = (props) => {
  return <h1>Hello, {props.name}!</h1>;
};
\`\`\`

### Class Components (Legacy)
The traditional way, still supported but less common:

\`\`\`jsx
class Greeting extends React.Component {
  render() {
    return <h1>Hello, {this.props.name}!</h1>;
  }
}
\`\`\`

## JSX: JavaScript XML

JSX allows you to write HTML-like syntax in JavaScript:

\`\`\`jsx
const element = <h1>Hello, World!</h1>;

// JSX with expressions
const name = 'React';
const element = <h1>Hello, {name}!</h1>;

// JSX with attributes
const element = <img src="logo.png" alt="Logo" />;
\`\`\`

## Props: Component Properties

Props are how data flows from parent to child components:

\`\`\`jsx
function UserCard(props) {
  return (
    <div className="user-card">
      <h2>{props.name}</h2>
      <p>{props.email}</p>
      <span>Age: {props.age}</span>
    </div>
  );
}

// Using the component
<UserCard 
  name="Alice Johnson" 
  email="alice@example.com" 
  age={28} 
/>
\`\`\`

## Component Composition

Build complex UIs by composing simple components:

\`\`\`jsx
function App() {
  return (
    <div>
      <Header />
      <Main>
        <Sidebar />
        <Content />
      </Main>
      <Footer />
    </div>
  );
}
\`\`\`
  `,
  duration: '60 minutes',
  difficulty: 'beginner',
  order: 2,
  estimatedTime: '60 minutes',
  objectives: [
    'Understand the difference between functional and class components',
    'Learn JSX syntax and how to embed expressions',
    'Master props and component communication',
    'Practice component composition patterns'
  ],
  learningObjectives: [
    'Understand the difference between functional and class components',
    'Learn JSX syntax and how to embed expressions',
    'Master props and component communication',
    'Practice component composition patterns'
  ],
  quiz: {
    questions: [
      {
        id: 'react-comp-1',
        question: 'What is a React component?',
        options: [
          'A reusable piece of UI that returns JSX',
          'A CSS class',
          'A JavaScript variable',
          'An HTML tag'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0,
        difficulty: 'easy',
        explanation: 'A React component is a reusable piece of code that returns JSX to describe what should appear on the screen. Components are the building blocks of React applications.',
        category: 'components',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: 'react-comp-2',
        question: 'What does JSX stand for?',
        options: [
          'JavaScript XML',
          'Java Syntax Extension',
          'JavaScript eXtension',
          'JSON XML'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0,
        difficulty: 'easy',
        explanation: 'JSX stands for JavaScript XML. It\'s a syntax extension for JavaScript that allows you to write HTML-like code within JavaScript, making React components more readable and easier to write.',
        category: 'jsx',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: 'react-comp-3',
        question: 'How do you create a functional component in React?',
        options: [
          'Using a JavaScript function that returns JSX',
          'Using the class keyword',
          'Using an HTML template',
          'Using CSS modules'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0,
        difficulty: 'medium',
        explanation: 'Functional components are created using JavaScript functions (either function declarations or arrow functions) that return JSX. This is the modern and recommended approach for creating React components.',
        category: 'functional-components',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: 'react-comp-4',
        question: 'What are props in React?',
        options: [
          'Properties used to pass data from parent to child components',
          'Functions that handle user events',
          'Variables that store component state',
          'CSS styles applied to components'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0,
        difficulty: 'medium',
        explanation: 'Props (properties) are a way to pass data from parent components to child components in React. They are read-only and help make components reusable by allowing different data to be passed in.',
        category: 'props',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: 'react-comp-5',
        question: 'Which of the following is valid JSX?',
        options: [
          '<div className="container">Hello World</div>',
          '<div class="container">Hello World</div>',
          '<div className=container>Hello World</div>',
          '<Div className="container">Hello World</Div>'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0,
        difficulty: 'medium',
        explanation: 'In JSX, you must use className instead of class (since class is a reserved keyword in JavaScript), and attribute values should be in quotes. JSX is case-sensitive, so HTML tags should be lowercase.',
        category: 'jsx-syntax',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: 'react-comp-6',
        question: 'What must a React component function return?',
        options: [
          'JSX, null, or other valid React elements',
          'A string only',
          'A number only',
          'An object only'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0,
        difficulty: 'medium',
        explanation: 'React components must return JSX elements, null (to render nothing), or other valid React elements like strings, numbers, or arrays of elements.',
        category: 'return-values',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: 'react-comp-7',
        question: 'How do you embed JavaScript expressions in JSX?',
        options: [
          'Using curly braces: {expression}',
          'Using square brackets: [expression]',
          'Using parentheses: (expression)',
          'Using angle brackets: <expression>'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0,
        difficulty: 'hard',
        explanation: 'JavaScript expressions are embedded in JSX using curly braces {}. This allows you to include variables, function calls, and other JavaScript code within your JSX markup.',
        category: 'jsx-expressions',
        points: 20,
        type: 'multiple-choice'
      },
      {
        id: 'react-comp-8',
        question: 'What is component composition in React?',
        options: [
          'Building complex UIs by combining simpler components',
          'Writing components in multiple files',
          'Using CSS to style components',
          'Creating components with class inheritance'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0,
        difficulty: 'hard',
        explanation: 'Component composition is the practice of building complex user interfaces by combining simpler, reusable components. This approach promotes code reusability and maintainability.',
        category: 'composition',
        points: 20,
        type: 'multiple-choice'
      }
    ],
    passThreshold: 80,
    timeLimit: 600,
    category: 'react'
  },
  quickCheck: {
    questions: [
      {
        id: 'react-comp-quick-1',
        question: 'What is a React component?',
        options: [
          'A reusable piece of UI',
          'A CSS file',
          'A database table',
          'A server function'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0
      }
    ],
    passThreshold: 80
  }
};
